// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { THDropdown } from '@Molecules/Inputs/THDropdown/THDropdown';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import { THMenu } from '@Molecules/THMenu/THMenu';
import { ListActionScopeType, ListActionType } from '@Types';
import React, { ReactElement } from 'react';

import './ActionsList.scss';

interface ActionsListProps {
    actionList: {
        [key: string]: ListActionType;
    };
    scope: ListActionScopeType;
    isIcon?: boolean;
}

export const ActionsList = ({ actionList, scope, isIcon }: ActionsListProps): ReactElement | null => {
    const i = useLocalMessage();

    const showActionList = Object.values(actionList).some(({ hidden }) => !hidden);

    return showActionList ? (
        <THDropdown
            triggerElem={(visible) => (
                <div
                    className={classList('actions-list', {
                        'dropdown-visible': visible,
                        'is-icon': isIcon,
                    })}
                >
                    {isIcon ? (
                        <ICONS.ActionIcon />
                    ) : (
                        <>
                            {i('common.action-list')} <ICONS.DownOutlined className="actions-list__arrow" />{' '}
                        </>
                    )}
                </div>
            )}
            trigger={['click']}
        >
            {Object.entries(actionList).map(
                ([
                    key,
                    {
                        icon,
                        label,
                        hidden,
                        CustomComponent,
                        query,
                        scope: actionScope = 'both',
                        disabled,
                        confirm,
                        message,
                    },
                ]) => {
                    const hideAction = hidden || (actionScope !== scope && actionScope !== 'both');

                    if (hideAction) {
                        return null;
                    }

                    const Item = (
                        <THMenu.Item
                            disabled={disabled}
                            key={key}
                            className="actions-list__item"
                            onClick={() => !confirm && query?.()}
                        >
                            {icon}
                            {label}
                        </THMenu.Item>
                    );

                    return confirm ? (
                        <THPopConfirm key={key} onConfirm={() => query?.()} title={message}>
                            {Item}
                        </THPopConfirm>
                    ) : CustomComponent ? (
                        React.cloneElement(CustomComponent(Item), { key })
                    ) : (
                        Item
                    );
                },
            )}
        </THDropdown>
    ) : null;
};
